/**
 * Task-scoped crew chat (demo #7) — a Salesforce-"Chatter"-style message board
 * on a single task. Crew assigned to the task plus supervisors/admins post short
 * messages and @mention each other; an @mention emails the mentioned Vari users
 * a link back to the task's chat.
 *
 * This is DISTINCT from the requester-facing "Updates"/activity feed: the
 * PM/requester is never a participant or recipient. v1 is task-level only.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
/**
 * An attachment riding along with a message (demo #7). References an existing
 * `attachments` row — either a file uploaded straight to the message or an
 * existing project document referenced via `@`. Rendering is unified: images
 * show as a thumbnail, everything else as a file/doc chip.
 */
export const TaskMessageAttachmentSchema = z.object({
    id: UuidSchema,
    filename: z.string(),
    content_type: z.string().nullable(),
    /** Signed, expiring download URL — what the <img>/<a> uses. */
    file_url: z.string().nullable(),
    /** Attachment kind hint (sales_order/photo/other/…), for the chip label. */
    kind: z.string().nullable(),
    /** True when the content type is an image — render as a thumbnail, not a chip. */
    is_image: z.boolean(),
});
/** One posted message, joined to its author for rendering. */
export const TaskMessageSchema = z.object({
    id: UuidSchema,
    /**
     * The task this message was posted on, or null for a project-level message
     * (D-3/D1: chat moved from the task up to the project). Legacy rows keep their
     * task_id as historical context; new project-chat messages carry none.
     */
    task_id: UuidSchema.nullable(),
    project_id: UuidSchema,
    author_user_id: UuidSchema.nullable(),
    author_display_name: z.string(),
    author_avatar_url: z.string().url().nullable(),
    body: z.string(),
    /** Users resolved from the body's @mentions at post time. */
    mentioned_user_ids: z.array(UuidSchema),
    /** Resolved attachments (uploaded files + referenced project docs). */
    attachments: z.array(TaskMessageAttachmentSchema),
    created_at: IsoDateTimeSchema,
});
/** POST body — the raw text the author typed; mentions are parsed server-side. */
export const TaskMessageCreateSchema = z.object({
    body: z.string().min(1).max(4000),
    /**
     * Optional explicit mention targets the composer already resolved (the
     * autocomplete picks). The server still re-parses the body and unions these
     * in, so a hand-typed "@Name" works even without a picker selection.
     */
    mentioned_user_ids: z.array(UuidSchema).max(50).optional(),
    /**
     * Attachment ids to pin to this message — files uploaded to the composer plus
     * project documents picked via `@`. The server validates each id belongs to
     * this task or project before storing (arbitrary ids are rejected).
     */
    attachment_ids: z.array(UuidSchema).max(20).optional(),
});
/**
 * A person who may be @mentioned on a task — the assigned crew plus
 * supervisors/admins. Returned alongside the message list so the composer can
 * autocomplete against exactly the eligible (crew-only) audience.
 */
export const TaskMessageParticipantSchema = z.object({
    user_id: UuidSchema,
    display_name: z.string(),
    avatar_url: z.string().url().nullable(),
    /** 'crew' | 'supervisor' | 'admin' — drives a small role hint in the picker. */
    role: z.string(),
});
export const TaskMessageListResponseSchema = z.object({
    /** Newest-last so the feed reads top-to-bottom like a chat transcript. */
    data: z.array(TaskMessageSchema),
    /** The @mention autocomplete source (crew + supervisors on this task). */
    participants: z.array(TaskMessageParticipantSchema),
    /**
     * The task's project id — so the composer can load that project's documents
     * for `@`-mentioning even when the chat has zero messages. Null only if the
     * task somehow has no project (defensive).
     */
    project_id: UuidSchema.nullable(),
});
//# sourceMappingURL=task-messages.js.map